import { Launch } from '@mui/icons-material'
import { Box, Link as MuiLink, TextField, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { ComponentProps } from 'react'
import { Link as RrdLink } from 'react-router-dom'
import {
  AppointmentCancellationReason,
  AppointmentType,
  Route,
  EventType,
  ResourceType,
  RouteParameters,
  SearchParameters,
} from 'constants/'
import { useAccount, useAppointment, useEventTracking, useFormat, useShops } from '../../../../hooks'
import { Appointment, AppointmentStatusDetail, CompletedAppointmentNewAppointmentLinkClickEventData, Resource } from '../../../../types'
import { appointmentTypeToDisplayText, hydratePathname } from '../../../../util'
import { AppointmentInfo } from '../../../AppointmentInfo'
import { Card } from '../../../Card'
import { CustomerName } from '../../../CustomerName'
import { Loader } from '../../../Loader'
import { Spacer } from '../../../Spacer'
import { Subtitle } from '../../../Subtitle'
import { Title } from '../../../Title'
import { Select as FormSelect } from '../../../inputs'
import { ImmutableAppointmentStatus } from '../types'
import { cancellationReasonDisplayText, rescheduleReasonDisplayText } from './utils'
import { statusToNoActionTitle } from '../constants'
import { useTranslation } from 'react-i18next'

const Select = styled(FormSelect)`
  max-width: 50%;
`

const { timeZone } = Intl.DateTimeFormat().resolvedOptions()

const rescheduleReasonOptions = Object.entries(rescheduleReasonDisplayText).map(([value, name]) => ({
  name,
  value,
}))

const cancellationReasonOptions = Object.values(AppointmentCancellationReason).map((reason) => ({
  name: cancellationReasonDisplayText[reason as keyof typeof cancellationReasonDisplayText],
  value: reason,
}))

const nonFitterResourceTypes = new Set([ResourceType.CABIN])
const isFitterResource = ({ type }: Resource) => nonFitterResourceTypes.has(type) === false

const Content = () => {
  const { appointment, isError: hasAppointmentErrored, isLoading: isAppointmentLoading } = useAppointment()
  const { t } = useTranslation()
  const { resources = [], status: statusDetails } = appointment ?? ({} as Partial<Appointment>)
  const { created_at, created_by, details, status } = statusDetails ?? ({} as Partial<AppointmentStatusDetail>)
  const resource = resources.find(isFitterResource)
  const { track: trackEvent } = useEventTracking()
  const { data: shops } = useShops({ id: resource?.shop_id as string }, { enabled: Boolean(resource?.shop_id) })
  const shop = shops?.[0]

  const { formatDate } = useFormat({
    dateOptions: {
      timeZone,
      month: 'short',
      day: 'numeric',
    },
  })

  const {
    account,
    isError: hasAccountErrored,
    isLoading: isAccountLoading,
  } = useAccount(created_by?.id as string, { enabled: Boolean(created_by?.id) })
  const title = statusToNoActionTitle[status as ImmutableAppointmentStatus]

  let accountDisplayText = 'Unknown'

  if (account) {
    accountDisplayText = `${account?.firstname} ${account?.lastname}`
  }

  let reasonLabel = ''
  let showReason = true
  let showScheduleNewLink = false
  let options: ComponentProps<typeof Select>['options'] = []

  switch (status) {
    case 'CANCELED':
      reasonLabel = 'pages.appointment.cancel.reasonForCancelling'
      options = cancellationReasonOptions
      break
    case 'COMPLETED':
      showReason = false
      showScheduleNewLink = true
      break
    case 'RESCHEDULED':
      reasonLabel = 'pages.appointment.reschedule.reason'
      options = rescheduleReasonOptions
      break
    default:
      showReason = false
      break
  }

  const pathname = hydratePathname(Route.CONSULTANT_SCHEDULE, {
    [RouteParameters.COUNTRY]: appointment?.country,
  })

  const url = new URL(pathname, window.location.href)

  const params = {
    [SearchParameters.APPOINTMENT_TYPE]: appointment?.type.appointment_type,
    [SearchParameters.JOURNEY]: appointment?.journey,
    [SearchParameters.OPPORTUNITY_GID]: appointment?.opportunity_gid,
    [SearchParameters.CUSTOMER_GID]: appointment?.customer.gid,
    [SearchParameters.RESOURCE_ID]: resource?.id,
    [SearchParameters.SHOP_GID]: shop?.gid,
  }

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value)
    }
  })

  const schedulingAssistantUrlSansOrigin = url.href.replace(url.origin, '')

  const handleCreateNewAppointmentLinkClick = () => {
    const data: CompletedAppointmentNewAppointmentLinkClickEventData = {
      appointmentId: appointment?.id as string,
      appointmentType: appointment?.type.appointment_type as AppointmentType,
    }

    trackEvent(EventType.APPOINTMENT_COMPLETE_NEW_APPOINTMENT_LINK_CLICK, {
      data,
      // We want to make sure this event is sent even if the user navigates away from the page
      options: { transport: 'sendBeacon' },
    })
  }

  const isLoading = isAppointmentLoading || isAccountLoading
  const hasErrored = hasAppointmentErrored || hasAccountErrored

  if (isLoading) {
    return (
      <Card>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Loader />
        </Box>
      </Card>
    )
  }

  if (hasErrored) {
    return (
      <Card>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography color="error">An error occurred while loading this appointment.</Typography>
        </Box>
      </Card>
    )
  }

  const appointmentTypeDisplayText = appointmentTypeToDisplayText[appointment?.type.appointment_type as AppointmentType]
  const linkText = `Click here to schedule a new ${appointmentTypeDisplayText}`

  return (
    <Card>
      <Title>{t(title)}</Title>
      <Spacer count={0.5} />
      <Subtitle>
        {t('pages.appointment.alreadyCompleted.subTitle', { resourceName: accountDisplayText, completedDate: formatDate(created_at) })}
      </Subtitle>
      <Spacer />
      <CustomerName />
      <Spacer count={0.25} />
      <AppointmentInfo />
      {showReason && (
        <>
          <Spacer count={1.5} />
          <Select disabled label={t(reasonLabel)} name="reason" options={options} value={details?.reason ?? ''} />
          <Spacer count={2.5} />
          <TextField
            disabled
            fullWidth
            InputLabelProps={{ shrink: true }}
            label={t('common.label.additionalInformation')}
            multiline
            name="description"
            rows={4}
            variant="outlined"
            value={details?.description ?? t('common.label.noAdditionalInformation')}
          />
        </>
      )}
      {showScheduleNewLink && (
        <>
          <Spacer count={3} />
          <MuiLink
            alignItems="center"
            component={RrdLink}
            display="flex"
            onClick={handleCreateNewAppointmentLinkClick}
            to={schedulingAssistantUrlSansOrigin}
            underline="hover"
            variant="body1"
          >
            {linkText}
            &nbsp;
            <Launch style={{ fontSize: '1.125rem' }} />
          </MuiLink>
        </>
      )}
    </Card>
  )
}

export { Content }
